import React from 'react';
import {
  Package, UtensilsCrossed, Users, DollarSign, Clock, CheckCircle, ChefHat, Truck,
  TrendingUp, Plus, Edit, Trash2, GripVertical, ArrowUp, ArrowDown, FolderOpen,
  Upload, Image, Star, MessageSquare, Mail, Eye, Reply, Send, Megaphone, Tag, Calendar, X, ChevronLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { apiRequest, formatPrice, getAuthToken } from '../../lib/utils';

const MenuTab = ({
  categories,
  deleteMenuItem,
  editingItem,
  handleMenuImageUpload,
  handleMenuSubmit,
  isMenuDialogOpen,
  menuForm,
  menuImageRef,
  menuItems,
  moveMenuItemDown,
  moveMenuItemUp,
  openEditDialog,
  resetMenuForm,
  setIsMenuDialogOpen,
  setMenuForm,
  uploadingMenuImage,
}) => (
          <TabsContent value="menu">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Menu Items</h2>
              <Dialog open={isMenuDialogOpen} onOpenChange={setIsMenuDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-[#FFC107] text-black hover:bg-[#F59E0B]"
                    onClick={resetMenuForm}
                    data-testid="add-menu-item"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#141417] border-[#27272A] max-w-lg">
                  <DialogHeader>
                    <DialogTitle>{editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleMenuSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Name</Label>
                        <Input
                          value={menuForm.name}
                          onChange={(e) => setMenuForm({...menuForm, name: e.target.value})}
                          required
                          className="bg-[#0B0B0C] border-[#27272A]"
                          data-testid="menu-form-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Price (Normal)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={menuForm.price}
                          onChange={(e) => setMenuForm({...menuForm, price: e.target.value})}
                          required
                          className="bg-[#0B0B0C] border-[#27272A]"
                          data-testid="menu-form-price"
                        />
                      </div>
                      {menuForm.category_id === 'cat-pizza' && (
                        <div className="space-y-2">
                          <Label>Price (Big)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={menuForm.price_big}
                            onChange={(e) => setMenuForm({...menuForm, price_big: e.target.value})}
                            placeholder="Leave empty if no big size"
                            className="bg-[#0B0B0C] border-[#27272A]"
                            data-testid="menu-form-price-big"
                          />
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={menuForm.description}
                        onChange={(e) => setMenuForm({...menuForm, description: e.target.value})}
                        required
                        className="bg-[#0B0B0C] border-[#27272A]"
                        data-testid="menu-form-description"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select
                          value={menuForm.category_id}
                          onValueChange={(value) => setMenuForm({...menuForm, category_id: value})}
                        >
                          <SelectTrigger className="bg-[#0B0B0C] border-[#27272A]" data-testid="menu-form-category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#141417] border-[#27272A]">
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>{cat.name_en || cat.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Spice Level (1-5)</Label>
                        <Input
                          type="number"
                          min="1"
                          max="5"
                          value={menuForm.spice_level || ''}
                          onChange={(e) => setMenuForm({...menuForm, spice_level: e.target.value})}
                          className="bg-[#0B0B0C] border-[#27272A]"
                          data-testid="menu-form-spice"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Ingredients (comma separated)</Label>
                      <Input
                        value={menuForm.ingredients}
                        onChange={(e) => setMenuForm({...menuForm, ingredients: e.target.value})}
                        placeholder="e.g. Cheese, Tomato, Lettuce, Onion"
                        className="bg-[#0B0B0C] border-[#27272A]"
                        data-testid="menu-form-ingredients"
                      />
                      <p className="text-xs text-zinc-500">Customers can deselect ingredients when ordering</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Image</Label>
                      <input
                        type="file"
                        ref={menuImageRef}
                        onChange={handleMenuImageUpload}
                        accept="image/*"
                        className="hidden"
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => menuImageRef.current?.click()}
                          disabled={uploadingMenuImage}
                          className="flex-1 border-zinc-700 hover:bg-[#25252E]"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingMenuImage ? 'Uploading...' : 'Upload Image'}
                        </Button>
                      </div>
                      {menuForm.image && (
                        <div className="mt-2">
                          <img 
                            src={menuForm.image} 
                            alt="Preview" 
                            className="w-full h-32 object-cover rounded-lg"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={menuForm.is_available}
                          onCheckedChange={(checked) => setMenuForm({...menuForm, is_available: checked})}
                          data-testid="menu-form-available"
                        />
                        <Label>Available</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={menuForm.is_popular}
                          onCheckedChange={(checked) => setMenuForm({...menuForm, is_popular: checked})}
                          data-testid="menu-form-popular"
                        />
                        <Label>Popular</Label>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-[#FFC107] text-black hover:bg-[#F59E0B]" data-testid="menu-form-submit">
                      {editingItem ? 'Update Item' : 'Add Item'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <Card className="bg-[#141417] border-[#27272A]">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#27272A]">
                      <TableHead>Image</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...menuItems].sort((a, b) => {
                      // First sort by category
                      const catA = categories.findIndex(c => c.id === a.category_id);
                      const catB = categories.findIndex(c => c.id === b.category_id);
                      if (catA !== catB) return catA - catB;
                      // Then by order within category
                      return (a.order || 0) - (b.order || 0);
                    }).map((item) => (
                      <TableRow key={item.id} className="border-[#27272A]" data-testid={`menu-row-${item.id}`}>
                        <TableCell>
                          {item.image ? (
                            <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded-lg" />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-[#1C1C22] flex items-center justify-center text-zinc-500 text-xs">No img</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-semibold">{item.name}</p>
                            {item.is_popular && <Badge className="bg-[#FFC107]/20 text-[#FFC107]">Popular</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const cat = categories.find(c => c.id === item.category_id);
                            return cat?.name_en || cat?.name || item.category_id;
                          })()}
                        </TableCell>
                        <TableCell className="text-[#FFC107]">
                          {formatPrice(item.price)}
                          {item.price_big && (
                            <span className="text-xs text-zinc-400 block">Big: {formatPrice(item.price_big)}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={item.is_available ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}>
                            {item.is_available ? 'Available' : 'Unavailable'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-zinc-400 hover:text-white"
                              onClick={() => moveMenuItemUp(item)}
                              data-testid={`move-up-${item.id}`}
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-zinc-400 hover:text-white"
                              onClick={() => moveMenuItemDown(item)}
                              data-testid={`move-down-${item.id}`}
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEditDialog(item)}
                              data-testid={`edit-${item.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-red-400 hover:text-red-300"
                              onClick={() => deleteMenuItem(item.id)}
                              data-testid={`delete-${item.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
);

export default MenuTab;
